/**
 * Auth modal — single-step. Renders Google's GIS button and Telegram's Login
 * Widget directly inside the modal. User picks a provider and authorizes in
 * one click instead of going through an intermediate "choose provider" screen.
 *
 * Mock-mode (VITE_USE_MOCK_AUTH=1) still routes to the local pickers so dev
 * doesn't depend on real Google/Telegram availability.
 */
import { ModalManager } from './manager';
import { icon } from '../utils/icons';
import { openGooglePicker } from './google-picker';
import { openTelegramMock } from './telegram-mock';
import { Auth } from '../auth';
import { t, getLocale, withLocale } from '../i18n';
import { showToast } from '../toast';
import {
  isPasskeySupported,
  hasLocalPasskeyFlag,
  isSetupDismissed,
  markSetupDismissed,
  authenticateWithPasskey,
  registerPasskey,
  reconcilePasskeyFlag,
} from '../passkeys';

const USE_MOCK = import.meta.env.VITE_USE_MOCK_AUTH === '1';

// "Try without an account (dev preview)" — guest login via /api/auth/dev.
// That endpoint is hard-disabled in production by server boot (ALLOW_DEV_AUTH=1
// crashes the process when NODE_ENV=production). Show only in dev.
const SHOW_DEV_GUEST = import.meta.env.DEV;

// Google Identity Services script. The `?hl=<locale>` query is the only
// reliable way to force the button's language — `renderButton({ locale })`
// is treated as a hint that loses to the user's Google-account language
// preference. See:
// https://developers.google.com/identity/gsi/web/reference/js-reference#locale
//
// We bake the locale into the script URL on first load. Locale switches
// after the script has loaded won't re-translate the button (Google caches
// it), but that's fine for a sign-in modal — page reload picks up the new
// locale and re-loads the script with the new ?hl=.
const GIS_BASE = 'https://accounts.google.com/gsi/client';
const TG_WIDGET_SRC = 'https://telegram.org/js/telegram-widget.js?22';
const TITLE_ID = 'auth-modal-title';

// ─── Google Identity Services lazy-loader ──────────────────────────────────
interface GoogleAccountsId {
  initialize(opts: { client_id: string; callback: (resp: { credential: string }) => void; ux_mode?: string }): void;
  renderButton(el: HTMLElement, opts: Record<string, unknown>): void;
  cancel?(): void;
}
declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
    onTelegramAuth?: (user: TelegramAuthPayload) => void;
  }
}

let gisLoading: Promise<GoogleAccountsId> | null = null;
/**
 * Width for Google's rendered button: the content width of the dialog or
 * settings group it sits in, 200–320px. Measured on the container, not the
 * mount's parent: that parent is a grid cell which content (the 320px button)
 * had already stretched.
 */
export function googleButtonWidth(mount: HTMLElement): number {
  const box = mount.closest<HTMLElement>('.modal-panel, .dash-settings__group') ?? mount.parentElement ?? mount;
  const cs = getComputedStyle(box);
  const avail = Math.floor(box.clientWidth - parseFloat(cs.paddingLeft || '0') - parseFloat(cs.paddingRight || '0'));
  return Math.max(200, Math.min(320, avail || 320));
}

function loadGis(): Promise<GoogleAccountsId> {
  if (window.google?.accounts?.id) return Promise.resolve(window.google.accounts.id);
  if (gisLoading) return gisLoading;
  gisLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `${GIS_BASE}?hl=${encodeURIComponent(getLocale())}`;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    s.onload = () => {
      const id = window.google?.accounts?.id;
      if (id) resolve(id);
      else reject(new Error('GIS loaded but accounts.id missing'));
    };
    document.head.appendChild(s);
  });
  return gisLoading;
}

// ─── Telegram widget payload typing (matches Telegram's onauth callback) ──
interface TelegramAuthPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

// ─── Public entry point ────────────────────────────────────────────────────

/**
 * Module-scoped state — protects against auto-prompt loops within a
 * single un-authed attempt while still letting fresh sign-in attempts
 * after sign-out get the auto-trigger UX.
 *
 * `autoTriedSinceLastAuth`: true after the auto-passkey path has been
 * attempted once. Subsequent `openAuth` calls skip auto-trigger and
 * go straight to the modal — prevents looping the Face ID prompt if
 * the user cancels and immediately re-taps Sign in.
 *
 * `lastTryWasUserCancelled`: true when the user explicitly cancelled
 * the Face ID prompt. We hide the welcome-back banner in that case —
 * showing it again would be nag-y. User can retry via /dashboard/
 * settings.
 *
 * **Reset policy** (sign-OUT only — see `Auth.onChange` listener
 * below): a user who:
 *   1. signs in via Face ID (autoTriedSinceLastAuth → true)
 *   2. uses the app
 *   3. signs out (no page reload — Auth.onChange fires with null)
 *   4. taps Sign in again
 * gets a fresh auto-trigger because the listener detects the
 * authed→unauthed transition and resets the flags. Sign-IN does NOT
 * reset, because resetting after a successful sign-in opens a race
 * window where a duplicate openAuth call could re-fire the Face ID
 * prompt or stack a duplicate modal. Operator caught the early
 * "reset on every change" approach 2026-05-09 поздний вечер +9.
 */
let autoTriedSinceLastAuth = false;
let lastTryWasUserCancelled = false;

// In-flight guard: while an auto-trigger passkey auth is awaiting iOS's
// Face ID prompt + the /verify round-trip, additional `openAuth` calls
// are no-ops. Without this guard, a second invocation (e.g. user taps
// "Sign in" again because they don't see anything happening, or a route
// re-resolve fires roamzy:open-login while the first call is in flight)
// would short-circuit through the "already tried" branch and pop a
// duplicate modal — which then doesn't get fully closed when the auth
// finally succeeds. Operator caught this 2026-05-09 поздний вечер +9.
let autoAuthInFlight = false;

// Reset state whenever auth transitions to UNAUTHED (sign-out). We
// previously reset on every auth change, but that fired immediately after
// successful sign-in too — turning a flag we'd just set back off and
// opening the door to double-trigger races. Sign-in doesn't need a reset
// (we just succeeded; no next attempt); only sign-out does.
let prevAuthIsAuthed: boolean | null = null;
Auth.onChange((user) => {
  const nowAuthed = user !== null;
  if (prevAuthIsAuthed === true && !nowAuthed) {
    // sign-out detected — clear all session-scoped flags so the user's
    // next sign-in attempt gets a fresh auto-trigger chance.
    autoTriedSinceLastAuth = false;
    lastTryWasUserCancelled = false;
    autoAuthInFlight = false;
  }
  prevAuthIsAuthed = nowAuthed;
});

export function openAuth(opts: { redirectTo?: string } = {}): void {
  // Auto-trigger Face ID / system password if the user has a passkey
  // registered on this device. Saves the user a click — the modal
  // doesn't even appear if biometric succeeds. Falls back gracefully
  // to the modal on cancel / failure / browser-blocks-without-gesture.
  // Operator UX request 2026-05-09 поздний вечер +6.
  // In-flight guard — caller race or double-tap should not stack modals
  // or fire a second WebAuthn prompt. Drop the duplicate call silently;
  // the original auto-trigger will resolve on its own.
  if (autoAuthInFlight) {
    return;
  }

  if (
    !autoTriedSinceLastAuth &&
    typeof navigator !== 'undefined' && navigator.onLine !== false &&
    isPasskeySupported() &&
    hasLocalPasskeyFlag()
  ) {
    autoTriedSinceLastAuth = true;
    autoAuthInFlight = true;
    void authenticateWithPasskey()
      .then(async (r) => {
        if (r.ok) {
          await Auth.init();
          if (opts.redirectTo) {
            window.history.pushState({}, '', withLocale(opts.redirectTo));
            window.dispatchEvent(new PopStateEvent('popstate'));
          } else {
            // No redirectTo (user clicked Sign in from a public page) —
            // re-resolve current route so any auth-gated UI re-renders.
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
          return;
        }
        // Auto-attempt failed — open the modal as fallback. Banner
        // visibility depends on why we failed:
        //   user_cancelled → hide banner (user just said no, don't nag)
        //   no_credentials → clear flag, hide banner (stale local flag)
        //   other (verify_failed, options_failed, unknown) → show banner
        //     so the user can retry; OAuth widgets are also available
        if (r.reason === 'user_cancelled') {
          lastTryWasUserCancelled = true;
        } else if (r.reason === 'no_credentials') {
          // `authenticateWithPasskey` already cleared the flag in this
          // case — extra defensive: we'll re-check `hasLocalPasskeyFlag`
          // when rendering the modal.
        }
        openAuthModalInternal(opts);
      })
      .finally(() => {
        autoAuthInFlight = false;
      });
    return;
  }
  openAuthModalInternal(opts);
}

function openAuthModalInternal(opts: { redirectTo?: string }): void {
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const telegramBotUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME;

  ModalManager.open({
    id: 'auth',
    panelClass: 'auth-modal',
    labelledBy: TITLE_ID,
    render() {
      // Offline guard: Google Identity Services and Telegram widget
      // both fetch JS from third-party CDNs, which obviously fails with
      // no network. Operator caught the broken-widgets state on iPhone
      // airplane mode 2026-05-09 — instead of showing two stuck
      // "Couldn't load X" lines, render a clean offline state and
      // suggest the user reconnect.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return `
<div class="auth-modal__head">
  <h2 id="${TITLE_ID}" class="auth-modal__title">${t('auth.title')}</h2>
  <p class="auth-modal__sub">${t('auth.tagline')}</p>
</div>
<div class="auth-modal__offline">
  <div class="auth-modal__offline-icon" aria-hidden="true">${icon('signal', 36)}</div>
  <p class="auth-modal__offline-title">${t('auth.offlineTitle')}</p>
  <p class="auth-modal__offline-body">${t('auth.offlineBody')}</p>
</div>`;
      }
      // Welcome-back fast path (mig 0040, 2026-05-09): if the user has
      // previously registered a passkey on this device, surface a Face ID
      // sign-in CTA at the top of the modal. OAuth widgets stay below as
      // the universal fallback. Only renders when the browser actually
      // supports WebAuthn AND we're not in Telegram Mini App context.
      //
      // Hidden after `lastTryWasUserCancelled` — the auto-trigger path
      // already prompted Face ID and the user said no; showing the same
      // CTA again would be nag-y. They can still retry via /dashboard/
      // settings or by reloading.
      const showPasskeyWelcome =
        !lastTryWasUserCancelled &&
        isPasskeySupported() &&
        hasLocalPasskeyFlag();
      const passkeyBlock = showPasskeyWelcome
        ? `<div class="auth-modal__passkey" data-passkey-block>
  <button type="button" class="btn btn--accent btn--lg auth-modal__passkey-btn" data-action="passkey-signin">
    ${icon('lock', 16)} ${t('auth.passkey.signinButton')}
  </button>
  <p class="auth-modal__passkey-status" data-passkey-status></p>
  <div class="auth-modal__divider"><span>${t('auth.passkey.signinOther')}</span></div>
</div>`
        : '';

      return `
<div class="auth-modal__head">
  <h2 id="${TITLE_ID}" class="auth-modal__title">${showPasskeyWelcome ? t('auth.passkey.welcomeBackTitle') : t('auth.title')}</h2>
  <p class="auth-modal__sub">${showPasskeyWelcome ? t('auth.passkey.welcomeBackBody') : t('auth.tagline')}</p>
</div>
${passkeyBlock}
<div class="auth-modal__widgets">
  <div class="auth-modal__widget" data-provider="google">
    <div id="auth-google-mount" class="auth-modal__widget-mount"></div>
    <p class="auth-modal__widget-status" data-status="google"></p>
  </div>
  <div class="auth-modal__widget" data-provider="telegram">
    <div id="auth-telegram-mount" class="auth-modal__widget-mount"></div>
    <p class="auth-modal__widget-status" data-status="telegram"></p>
  </div>
</div>
${SHOW_DEV_GUEST ? `<div class="auth-modal__divider"><span>${t('auth.or')}</span></div>
<button class="auth-modal__guest" data-provider="guest">${t('auth.guestCta')}</button>
<p class="auth-modal__guest-note">${t('auth.guestNote')}</p>` : ''}
<p class="auth-modal__legal">
  ${t('auth.legalAgree')} <a href="/legal/terms">${t('auth.legalTerms')}</a> &amp; <a href="/legal/privacy">${t('auth.legalPrivacy')}</a>.
</p>`;
    },
    onMount(panel) {
      // If we rendered the offline state, there are no widgets to wire.
      // Listen for the `online` event to close the modal so the next
      // open hits the normal render path with working widgets. (Auto-
      // reopening would be presumptuous; let the user re-tap Sign in.)
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        const onOnline = (): void => ModalManager.close('auth');
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
      }
      const googleMount = panel.querySelector<HTMLElement>('#auth-google-mount');
      const telegramMount = panel.querySelector<HTMLElement>('#auth-telegram-mount');
      const googleStatus = panel.querySelector<HTMLElement>('[data-status="google"]');
      const telegramStatus = panel.querySelector<HTMLElement>('[data-status="telegram"]');

      // ── Successful-signin handler shared by Google + Telegram callbacks ──
      // After OAuth completes successfully, decide whether to:
      //   (a) close modal + redirect (user already has a passkey, or device
      //       doesn't support, or user dismissed setup before)
      //   (b) replace modal content with "Set up Face ID" prompt
      // Path (b) only the first time we see this device-without-passkey for
      // a freshly-signed-in user. After dismissing once, we don't nag again.
      //
      // Reconciliation step before deciding (operator pass +15): ask the
      // server if this user already has any passkey credentials. If yes —
      // update local flag and skip the prompt (avoids nagging on Mac when
      // user registered passkey on iPhone — iCloud Keychain syncs the
      // credential, but localStorage doesn't sync).
      const finishAuthFlow = async (): Promise<void> => {
        if (isPasskeySupported() && !hasLocalPasskeyFlag()) {
          await reconcilePasskeyFlag();
        }
        if (
          isPasskeySupported() &&
          !hasLocalPasskeyFlag() &&
          !isSetupDismissed()
        ) {
          renderPasskeySetupPrompt(panel, opts);
          return;
        }
        ModalManager.close('auth');
        if (opts.redirectTo) {
          window.history.pushState({}, '', withLocale(opts.redirectTo));
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      };

      // ── Welcome-back: Face ID button at top of modal ──
      const passkeyBtn = panel.querySelector<HTMLButtonElement>('[data-action="passkey-signin"]');
      const passkeyStatus = panel.querySelector<HTMLElement>('[data-passkey-status]');
      if (passkeyBtn) {
        passkeyBtn.addEventListener('click', () => {
          if (passkeyStatus) passkeyStatus.textContent = t('auth.verifying');
          passkeyBtn.disabled = true;
          void authenticateWithPasskey().then(async (r) => {
            if (r.ok) {
              await Auth.init();
              ModalManager.close('auth');
              if (opts.redirectTo) {
                window.history.pushState({}, '', withLocale(opts.redirectTo));
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
              return;
            }
            // user_cancelled — stay quiet, just re-enable button
            // no_credentials — credential gone server-side; clear flag & re-render the modal so OAuth shows
            // others — show error, let user fall back to OAuth widgets below
            passkeyBtn.disabled = false;
            if (r.reason === 'user_cancelled') {
              if (passkeyStatus) passkeyStatus.textContent = '';
              return;
            }
            if (r.reason === 'no_credentials') {
              // Hide the welcome-back block so the user sees normal OAuth options
              const block = panel.querySelector<HTMLElement>('[data-passkey-block]');
              if (block) block.hidden = true;
              return;
            }
            if (passkeyStatus) passkeyStatus.textContent = t('auth.passkey.failedBody');
          });
        });
      }

      // ── Google widget ──
      if (googleMount && googleClientId && !USE_MOCK) {
        void loadGis()
          .then((gis) => {
            gis.initialize({
              client_id: googleClientId,
              callback: (resp) => {
                if (googleStatus) googleStatus.textContent = t('auth.verifying');
                void (async () => {
                  const { authPayloadExtras } = await import('../utm');
                  return fetch('/api/auth/google', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({ idToken: resp.credential, ...authPayloadExtras() }),
                  });
                })()
                  .then(async (r) => {
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    await Auth.init();
                    await finishAuthFlow();
                  })
                  .catch((err) => {
                    if (googleStatus) googleStatus.textContent = `${t('auth.signInFailed')} ${(err as Error).message}`;
                  });
              },
              ux_mode: 'popup',
            });
            gis.renderButton(googleMount, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'continue_with',
              shape: 'rectangular',
              logo_alignment: 'center',
              locale: getLocale(),
              // Google renders at a fixed pixel width (200–400). 320 was wider
              // than the modal's content box on a 375px phone and gave the sign-in
              // dialog a sideways scroll (2026-09-29): fit the space we have.
              width: googleButtonWidth(googleMount),
            });
          })
          .catch((err) => {
            if (googleStatus) googleStatus.textContent = `${t('auth.googleLoadFailed')} ${(err as Error).message}`;
          });
      } else if (googleMount && USE_MOCK) {
        // Dev: render a clickable shim that opens the local picker.
        googleMount.innerHTML = `<button type="button" class="auth-provider auth-provider--google" data-mock="google">
          <span class="auth-provider__icon" aria-hidden="true">G</span>
          <span class="auth-provider__label">${t('auth.continueGoogle')}</span>
        </button>`;
      }

      // ── Telegram widget ──
      if (telegramMount && telegramBotUsername && !USE_MOCK) {
        // Wire the global callback Telegram's widget invokes after Authorize.
        window.onTelegramAuth = (payload: TelegramAuthPayload): void => {
          if (telegramStatus) telegramStatus.textContent = t('auth.verifying');
          void (async () => {
            const { authPayloadExtras } = await import('../utm');
            return fetch('/api/auth/telegram', {
              method: 'POST',
              credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ ...payload, ...authPayloadExtras() }),
            });
          })()
            .then(async (r) => {
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              await Auth.init();
              finishAuthFlow();
            })
            .catch((err) => {
              if (telegramStatus) telegramStatus.textContent = `${t('auth.signInFailed')} ${(err as Error).message}`;
            });
        };
        const s = document.createElement('script');
        s.src = TG_WIDGET_SRC;
        s.async = true;
        s.setAttribute('data-telegram-login', telegramBotUsername);
        s.setAttribute('data-size', 'large');
        // Match Google's `shape: 'rectangular'` button. Telegram's default is a
        // 20px pill, which read as a foreign control sitting next to ours
        // (operator, 2026-07-28). Radius is the one geometry knob the widget
        // exposes; its colour and label are fixed by Telegram's brand rules.
        s.setAttribute('data-radius', '4');
        s.setAttribute('data-onauth', 'onTelegramAuth(user)');
        s.setAttribute('data-request-access', 'write');
        s.setAttribute('data-lang', getLocale());
        s.onerror = () => {
          if (telegramStatus) telegramStatus.textContent = t('auth.tgWidgetFailed');
        };
        telegramMount.appendChild(s);
      } else if (telegramMount && USE_MOCK) {
        telegramMount.innerHTML = `<button type="button" class="auth-provider auth-provider--telegram" data-mock="telegram">
          <span class="auth-provider__icon" aria-hidden="true">${icon('send', 15)}</span>
          <span class="auth-provider__label">${t('auth.continueTelegram')}</span>
        </button>`;
      }

      // ── Click handlers for mock-shims and the dev guest button ──
      const onClick = (e: Event): void => {
        const target = e.target as Element | null;
        const mockBtn = target?.closest<HTMLElement>('[data-mock]');
        if (mockBtn) {
          const provider = mockBtn.dataset.mock;
          ModalManager.close('auth');
          if (provider === 'google') openGooglePicker(opts);
          else if (provider === 'telegram') openTelegramMock(opts);
          return;
        }
        const guestBtn = target?.closest<HTMLElement>('[data-provider="guest"]');
        if (guestBtn) {
          ModalManager.close('auth');
          void Auth.loginDev(`guest-${Date.now()}`)
            .then(() => {
              const dest = opts.redirectTo ?? '/dashboard/esims';
              window.history.pushState({}, '', withLocale(dest));
              window.dispatchEvent(new PopStateEvent('popstate'));
            })
            .catch((err) => {
              showToast(`${t('toast.guestSignInFailed')} ${(err as Error).message}`, 'error');
            });
        }
      };
      panel.addEventListener('click', onClick);
      return () => {
        panel.removeEventListener('click', onClick);
        try {
          window.google?.accounts?.id?.cancel?.();
        } catch { /* ignore */ }
        try { delete window.onTelegramAuth; } catch { /* ignore */ }
      };
    },
  });
}

/**
 * After successful OAuth signin, replace the modal contents with a
 * one-time prompt offering to register a passkey. User can accept
 * (Face ID / Touch ID prompt → server stores credential → flag is set)
 * or dismiss (we mark `rz_passkey_setup_dismissed` so we don't nag
 * again — they can still opt in from /dashboard/settings).
 *
 * Either path closes the modal and performs the original redirect.
 */
function renderPasskeySetupPrompt(panel: HTMLElement, opts: { redirectTo?: string }): void {
  panel.innerHTML = `
<div class="auth-modal__head auth-modal__head--success">
  <div class="auth-modal__success-icon" aria-hidden="true">✓</div>
  <h2 id="${TITLE_ID}" class="auth-modal__title">${t('auth.passkey.setupTitle')}</h2>
  <p class="auth-modal__sub">${t('auth.passkey.setupBody')}</p>
</div>
<div class="auth-modal__passkey-cta">
  <button type="button" class="btn btn--accent btn--lg" data-action="passkey-setup-confirm">
    ${icon('lock', 16)} ${t('auth.passkey.setupButton')}
  </button>
  <button type="button" class="btn btn--ghost" data-action="passkey-setup-skip">
    ${t('auth.passkey.setupSkip')}
  </button>
  <p class="auth-modal__passkey-status" data-passkey-status></p>
</div>`;

  const finish = (): void => {
    ModalManager.close('auth');
    if (opts.redirectTo) {
      window.history.pushState({}, '', withLocale(opts.redirectTo));
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const setupBtn = panel.querySelector<HTMLButtonElement>('[data-action="passkey-setup-confirm"]');
  const skipBtn = panel.querySelector<HTMLButtonElement>('[data-action="passkey-setup-skip"]');
  const statusEl = panel.querySelector<HTMLElement>('[data-passkey-status]');

  setupBtn?.addEventListener('click', () => {
    setupBtn.disabled = true;
    if (skipBtn) skipBtn.disabled = true;
    void registerPasskey().then((r) => {
      if (r.ok) {
        showToast(t('auth.passkey.registeredToast'), 'success');
        finish();
        return;
      }
      // User cancelled biometric prompt — re-enable buttons, no error noise
      if (r.reason === 'user_cancelled') {
        setupBtn.disabled = false;
        if (skipBtn) skipBtn.disabled = false;
        if (statusEl) statusEl.textContent = '';
        return;
      }
      // Real failure — show error briefly, then proceed to the dashboard
      // anyway. User is signed in; passkey is bonus. They can retry from
      // /dashboard/settings.
      if (statusEl) statusEl.textContent = t('auth.passkey.failedBody');
      setTimeout(finish, 1500);
    });
  });

  skipBtn?.addEventListener('click', () => {
    markSetupDismissed();
    finish();
  });
}
